import React, { useState } from 'react';

import { Divider, Modal, Popover, Tag, message } from 'antd';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';

import AddressLine from '@components/Addresses/AddressLine';
import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { HiddenMetricContainer } from '@containers/User/UserStyle';

import NwLink from './NwLink';
import StyleVariables from './StyleVariables';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PopoverContent = styled.div`
    color: white;
    .email-item {
        padding: 5px 0;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        font-size: 12px;
        .email-text {
            margin-right: 20px;
        }
        .remove-email {
            margin-left: auto;
            cursor: pointer;
            user-select: none;
        }
    }
`;

const LineContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: ${(props) => (props.justify ? props.justify : 'flex-end')};
    align-items: center;
    color: ${StyleVariables.TextColor};

    .line-container-left {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: nowrap;
        gap: 0.5rem;

        p {
            text-transform: uppercase;
            margin: 0;

            &.low {
                text-transform: lowercase;
            }

            small {
                display: block;
                font-weight: 300;
                font-size: ${(props) => props.theme.FontSizeXSm};
                line-height: 1.75em;
            }
        }

        &.line-container-left-phone,
        &.line-container-left-mail {
            cursor: pointer;
            p {
                text-overflow: ellipsis;
                //max-height: 1.5rem;
                line-height: 1.25rem !important;
                //overflow: hidden;
                max-width: 18rem;
                font-size: 1rem !important;
            }
        }

        &.disabled {
            opacity: 0.5;
            cursor: default;

            &.line-container-left-phone,
            &.line-container-left-mail {
                cursor: default;
            }
        }
    }
`;

const KeepSpace = styled.div`
    min-width: 70px;
`;

const MagnifyModal = styled(Modal)`
    font-size: 3rem;
    color: #000;

    .ant-modal-close-x {
        font-size: 3rem;
        color: #000;
    }

    .ant-modal-content {
        background-color: rgba(255, 255, 255, 0.75);
    }

    .magnify-phone-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2rem;

        h1 {
            font-style: normal;
            font-size: 7.5vw;
            color: #000;
            margin: 0;
            white-space: nowrap;
        }

        p.phone-number-note {
            text-align: center;
            font-size: ${(props) => props.theme.FontSizeLg};
            font-weight: 300;
            text-transform: uppercase;
            margin-bottom: 0.5rem;
            color: ${(props) => props.theme.TextColor};
        }

        .magnify-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            width: 100%;
            max-width: 600px;
        }
    }
`;

const MobileActionsModal = styled(Modal)`
    .ant-modal-content {
        border-radius: 12px;
    }

    .mobile-phone-actions {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem 0;

        .phone-number-display {
            text-align: center;
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
            color: ${(props) => props.theme.TextColor};
        }
        .phone-number-note {
            text-align: center;
            font-size: ${(props) => props.theme.FontSizeBase};
            font-weight: 300;
            margin-bottom: 0.5rem;
            color: ${(props) => props.theme.TextColor};
        }
    }
`;

const InMailingListsContainer = styled.div`
    padding: 0.15rem 0 0.15rem 1.6rem;
    font-size: 0.8rem;
    font-weight: 300;
    svg {
        margin-right: 0.5rem;
    }
`;

const MailLinkString = (email, cc) => {
    let maillinkstring = `mailto:${email}`;
    if (cc) {
        maillinkstring += `?cc=${cc.join(',')}`;
    }
    return maillinkstring;
};

const MailLink = ({ email, cc, disabled, linkText }) => {
    const openMail = () => {
        window.location.href = MailLinkString(email, cc);
    };
    return linkText ? (
        <NwLink tooltip="Send an e-mail to this address" disabled={disabled} onClick={openMail}>
            {linkText}
        </NwLink>
    ) : (
        <NwButton
            ghost
            icon={byPrefixAndName.fasl['paper-plane']}
            tooltip="Send an e-mail to this address"
            disabled={disabled}
            onClick={openMail}
        />
    );
};

const PhoneTypeIcon = (type) => {
    if (!type) return byPrefixAndName.fasl['mobile'];
    switch (type.toLowerCase()) {
        case 'mobile':
            return byPrefixAndName.fasl['mobile'];
        case 'land':
            return byPrefixAndName.fasl['phone-office'];
        case 'fax':
            return byPrefixAndName.fasl['fax'];
        case 'other':
            return byPrefixAndName.fasl['phone'];
        default:
            return byPrefixAndName.fasl['mobile'];
    }
};

const PhoneLine = ({ phoneNumber, phoneType, phoneNote, disableMagnifier, noIcon, onClick, mode }) => {
    const [magnifier, setMagnifier] = useState(false);
    const { isMobile } = useLayoutContext();
    const hasPhone = () => phoneNumber && phoneNumber !== '';
    // const cursorStyle = onClick && hasPhone() ? { cursor: 'pointer' } : {};

    const handleClick = () => {
        if (!disableMagnifier) {
            setMagnifier(true);
        }
    };

    const handleClose = () => {
        setMagnifier(false);
    };

    return (
        <>
            {mode === 'button' ? (
                <NwButton
                    lowercase="true"
                    primary
                    icon={PhoneTypeIcon(phoneType)}
                    label={phoneNumber}
                    onClick={handleClick}
                />
            ) : (
                <LineContainer>
                    <div
                        className={`line-container-left line-container-left-phone${!hasPhone() ? ' disabled' : ''}`}
                        onClick={hasPhone() ? (noIcon ? handleClick : onClick) : undefined}
                    >
                        <span>
                            <NwIcon icon={PhoneTypeIcon(phoneType)} />
                        </span>
                        <p className={hasPhone() ? '' : 'low'}>
                            {hasPhone() ? phoneNumber : phoneType === 'Mobile' ? 'no mobile' : 'no land phone'}
                            {phoneNote && <small>{phoneNote}</small>}
                        </p>
                    </div>

                    {!noIcon && (
                        <>
                            {hasPhone() && !disableMagnifier ? (
                                <>
                                    <Divider type="vertical" />
                                    <div className="line-container-right">
                                        <PhoneMagnifier
                                            disabled={!hasPhone()}
                                            phone={hasPhone() ? phoneNumber : ''}
                                            phoneType={phoneType}
                                            phoneNote={phoneNote}
                                        />
                                    </div>
                                </>
                            ) : (
                                <KeepSpace />
                            )}
                        </>
                    )}
                </LineContainer>
            )}
            {magnifier && (
                <BigPhoneNumber
                    phone={phoneNumber}
                    phoneType={phoneType}
                    phoneNote={phoneNote}
                    onClose={handleClose}
                    isMobile={isMobile}
                />
            )}
        </>
    );
};

const InMailingLists = ({ mailingLists, onAddToMailingList }) => {
    if (mailingLists && mailingLists.length > 0) {
        return (
            <InMailingListsContainer className="nw-link" onClick={onAddToMailingList}>
                <NwIcon icon={byPrefixAndName.fasl['mailbox']} />
                in {mailingLists.length} mailing lists
            </InMailingListsContainer>
        );
    }
    return <></>;
};

const MailLine = ({ disableMailTo, groupCC, mailAddress, mailCC, mode, noIcon, onClick }) => {
    const hasMail = () => mailAddress && mailAddress !== '';
    const hasCC = () => hasMail() && mailCC && mailCC.length > 0;

    // const cursorStyle = onClick ? { cursor: 'pointer' } : {};

    const handleClick = () => {
        if (disableMailTo || !hasMail()) {
            return;
        }
        window.location.href = MailLinkString(mailAddress, mailCC);
    };

    return mode === 'button' ? (
        <NwButton
            lowercase="true"
            primary
            icon={byPrefixAndName.fasl['envelope']}
            label={hasMail() ? mailAddress : 'no e-mail'}
            disabled={!hasMail()}
            onClick={handleClick}
        />
    ) : (
        <LineContainer>
            <div
                className={`line-container-left line-container-left-mail${!hasMail() ? ' disabled' : ''}`}
                onClick={hasMail() ? (noIcon ? handleClick : onClick) : undefined}
            >
                <span>
                    <NwIcon icon={byPrefixAndName.fasl['envelope']} danger={!hasMail()} />
                </span>
                <p className="low">
                    {hasMail() ? mailAddress : 'no e-mail'}
                    {hasCC() &&
                        (groupCC || mailCC.length > 3 ? (
                            <Popover
                                overlayClassName="recipients-popover"
                                content={
                                    <PopoverContent>
                                        <div>CCs:</div>
                                        {mailCC &&
                                            mailCC.map((email, cIndex) => {
                                                return (
                                                    <div
                                                        key={cIndex}
                                                        className="email-item"
                                                        style={{ borderTop: cIndex ? '1px dashed white' : 'none' }}
                                                    >
                                                        <div className="email-text">{email}</div>
                                                    </div>
                                                );
                                            })}
                                    </PopoverContent>
                                }
                            >
                                <Tag
                                    style={{ marginLeft: '.25rem', display: 'inline' }}
                                    color={StyleVariables.PrimaryDark}
                                >
                                    {mailCC.length} CC
                                </Tag>
                            </Popover>
                        ) : (
                            <small>CC: {mailCC.join('; ')}</small>
                        ))}
                </p>
            </div>
            {!noIcon && (
                <>
                    {hasMail() ? (
                        <>
                            <Divider type="vertical" />
                            <div className="line-container-right">
                                <MailLink
                                    disabled={!hasMail()}
                                    email={hasMail() ? mailAddress : ''}
                                    cc={hasCC() && mailCC}
                                />
                            </div>
                        </>
                    ) : (
                        <KeepSpace />
                    )}
                </>
            )}
        </LineContainer>
    );
};

const ContactAddressLine = ({ address, compact, showAccess = true }) => {
    return (
        <LineContainer>
            <AddressLine address={address} compact={compact} showAccess={showAccess} />
        </LineContainer>
    );
};

const BigPhoneNumber = ({ phone, phoneType, phoneNote, onClose, isMobile }) => {
    const isMobilePhone = phoneType?.toLowerCase() === 'mobile';

    const handleCopyToClipboard = async () => {
        try {
            // Prova prima con l'API moderna
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(phone);
                message.success('Phone number copied to clipboard');
                onClose();
            } else {
                // Fallback per iOS e browser più vecchi
                const textArea = document.createElement('textarea');
                textArea.value = phone;
                textArea.style.position = 'fixed';
                textArea.style.top = '0';
                textArea.style.left = '0';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                    const successful = document.execCommand('copy');
                    if (successful) {
                        message.success('Phone number copied to clipboard');
                        onClose();
                    } else {
                        message.error('Failed to copy phone number');
                    }
                } catch (err) {
                    message.error('Failed to copy phone number');
                } finally {
                    document.body.removeChild(textArea);
                }
            }
        } catch (err) {
            message.error('Failed to copy phone number');
        }
    };

    const handleCall = () => {
        window.location.href = `tel:${phone}`;
        onClose();
    };

    const handleWhatsApp = () => {
        const cleanPhone = phone.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank');
        onClose();
    };

    if (isMobile) {
        return (
            <MobileActionsModal open={true} onCancel={onClose} footer={null} centered width="90%" closable={true}>
                <div className="mobile-phone-actions">
                    <div className="phone-number-display">{phone}</div>
                    {phoneNote && <div className="phone-number-note">{phoneNote}</div>}
                    <NwButton block primary icon={byPrefixAndName.fasl['phone']} label="Call" onClick={handleCall} />
                    {isMobilePhone && (
                        <NwButton
                            block
                            primary
                            icon={byPrefixAndName.fab['whatsapp']}
                            label="WhatsApp"
                            onClick={handleWhatsApp}
                        />
                    )}
                    <NwButton
                        block
                        primary
                        icon={byPrefixAndName.fasl['copy']}
                        label="Copy to Clipboard"
                        onClick={handleCopyToClipboard}
                    />
                </div>
            </MobileActionsModal>
        );
    }

    return (
        <MagnifyModal
            open={true}
            onCancel={onClose}
            footer={null}
            centered
            width="90vw"
            closable={true}
            closeIcon={<NwIcon icon={byPrefixAndName.fass['times']} size="2rem" style={{ color: '#000' }} />}
        >
            <div className="magnify-phone-content">
                <h1>{phone}</h1>
                {phoneNote && <p className="phone-number-note">{phoneNote}</p>}
                <div className="magnify-actions">
                    <NwButton primary icon={byPrefixAndName.fasl['phone']} label="Call" onClick={handleCall} />
                    {isMobilePhone && (
                        <NwButton
                            primary
                            icon={byPrefixAndName.fab['whatsapp']}
                            label="WhatsApp"
                            onClick={handleWhatsApp}
                        />
                    )}
                    <NwButton
                        primary
                        icon={byPrefixAndName.fasl['copy']}
                        label="Copy to Clipboard"
                        onClick={handleCopyToClipboard}
                    />
                </div>
            </div>
        </MagnifyModal>
    );
};

const PhoneMagnifier = ({ phone, phoneType, phoneNote, disabled, readOnly, linkText }) => {
    const [magnifier, setMagnifier] = useState(false);
    const { isMobile } = useLayoutContext();

    const handleClose = () => {
        setMagnifier(false);
    };

    return (
        <>
            {!readOnly &&
                (linkText ? (
                    <NwLink
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            setMagnifier(true);
                        }}
                    >
                        {linkText}
                    </NwLink>
                ) : (
                    <NwButton
                        ghost
                        icon={byPrefixAndName.fasl['search-plus']}
                        tooltip="Show the phone number full screen"
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            setMagnifier(true);
                        }}
                    />
                ))}
            {magnifier && (
                <BigPhoneNumber
                    phone={phone}
                    phoneType={phoneType}
                    phoneNote={phoneNote}
                    onClose={handleClose}
                    isMobile={isMobile}
                />
            )}
        </>
    );
};

export { MailLink, PhoneMagnifier, PhoneLine, MailLine, InMailingLists, ContactAddressLine };
